import { Injectable } from '@angular/core';
import { ApiService, GetRequestArguments, PostRequestArguments, Refresh } from './api.service';
import { CacheService } from 'ionic-cache';
import { HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, forkJoin } from 'rxjs';
import { FormArray, UntypedFormBuilder, UntypedFormControl, Validators} from '@angular/forms';
import { FeedbackService } from './feedback.service';
import { NavController } from '@ionic/angular';
import { ProjectService } from './project.service';
import { CurrencyService } from './currency.service';
import { filter, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class CustomizationService {

  public $itemStateTransitions = new BehaviorSubject([]);
  public $itemStates = new BehaviorSubject([]);
  public $itemTypes = new BehaviorSubject([]);

  public $editingItemStateTransition = new BehaviorSubject({});
  public $newItemStateTransition = new BehaviorSubject({});

  public $vatRates = new BehaviorSubject([]);
  public $editingVatRate = new BehaviorSubject({});
  public $newVatRate = new BehaviorSubject({});

  public $roles = new BehaviorSubject([]);
  public $users = new BehaviorSubject([]);
  public $editingRoleUser = new BehaviorSubject({});
  public $newRoleUser = new BehaviorSubject({});

  public $apiTokens = new BehaviorSubject([]);
  public $editingAPIToken = new BehaviorSubject({});
  public $newAPIToken = new BehaviorSubject({});

  private url = 'customization';
  private groupKey = 'customization';
  private hour = 60 * 60;
  constructor(private api: ApiService,
              private cache: CacheService,
              private formBuilder: UntypedFormBuilder,
              private feedbackService: FeedbackService,
              private projectService: ProjectService,
              private currencyService: CurrencyService,
              private navController: NavController) {
  }

  public loadItemStateTransitions(refresh: Refresh = 'expired', itemTypeID) {
    const args = new GetRequestArguments({
      url: [this.url, 'itemStateTransitions', itemTypeID],
      cacheGroup: this.groupKey,
      ttl: this.hour * 24,
      refresh
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(ItemStateTransitions => {
      this.api.updateObservable(ItemStateTransitions, this.$itemStateTransitions);
    }); //automatically reload when new data arrives from API
    return call;
  }

  public loadItemStateTransition(itemStateTransitionId) {
    const args = new GetRequestArguments({
      url: [this.url, 'itemStateTransition', itemStateTransitionId], // back end
      cacheGroup: undefined,
    });
    this.api.cachedGetRequest(args).subscribe(
      itemStateTransition => {
        const form = this.createItemStateTransitionForm(itemStateTransition.item_type_id);
        this.fillItemStateTransitionForm(form, itemStateTransition);
        this.api.updateObservable(form, this.$editingItemStateTransition);
        this.loadItemStates('all', itemStateTransition.item_type_id);
      },
      error => {
        this.feedbackService.standardError(error, 4000, 'top').then( () => {
          this.navController.navigateBack(['/', 'customization', 'workflowActions']);
        });
      });
  }

  public loadItemTypes(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: ['itemTypes'],
      cacheGroup: this.groupKey,
      ttl: this.hour * 24,
      refresh
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(ItemTypes => {
      this.api.updateObservable(ItemTypes, this.$itemTypes);
    });
    return call;
  }


  public createItemStateTransitionForm(itemType_id?) {
    return this.formBuilder.group({
      created_at: [undefined, []],
      default: [undefined, []],
      deleted_at: [undefined, []],
      id: [undefined, []],
      name: [undefined, [Validators.required, Validators.minLength(2)]],
      updated_at: [undefined, []],

      item_type_id: [itemType_id ?? undefined, []],
      tech_key: [undefined, []],
      from_item_state_id: [undefined, [Validators.required]],
      to_item_state_id: [undefined, [Validators.required]],
      swipe_direction: ['', []],
      user_can_trigger: [undefined, []],
    });
  }

  public fillItemStateTransitionForm(form, item_state_transition) {
    if (item_state_transition !== undefined && item_state_transition !== null) {
      form.controls.created_at.patchValue(item_state_transition.created_at);
      form.controls.default.patchValue(item_state_transition.default);
      form.controls.deleted_at.patchValue(item_state_transition.deleted_at);
      form.controls.id.patchValue(parseInt(item_state_transition.id, 10));
      form.controls.name.patchValue(item_state_transition.name);
      form.controls.updated_at.patchValue(item_state_transition.updated_at);

      form.controls.item_type_id.patchValue(item_state_transition.item_type_id);
      form.controls.tech_key.patchValue(item_state_transition.tech_key);
      form.controls.from_item_state_id.patchValue(item_state_transition.from_item_state_id);
      form.controls.to_item_state_id.patchValue(item_state_transition.to_item_state_id);
      form.controls.swipe_direction.patchValue(item_state_transition.swipe_direction);
      form.controls.user_can_trigger.patchValue(item_state_transition.user_can_trigger);
    }
  }

  public createItemStateTransitionFormData(item_state_transition) {
    return this.api.createFormData(item_state_transition);
  }

  public postItemStateTransition(itemStateTransition, itemTypeID) {
    const args = new PostRequestArguments({
      url: [this.url, 'itemStateTransitions', itemTypeID],
      object: itemStateTransition,
    });
    const call = this.api.postRequest(args);
    call.subscribe(

      itemStateTransitions => {
        this.api.updateObservable(itemStateTransitions, this.$itemStateTransitions);
        this.api.getCacheUrl([this.url, 'itemStateTransitions']).then(
          url => { this.cache.saveItem(url, itemStateTransitions, this.groupKey, 60*60 * 24); }
        );
      },

      error => {
        // With this new modification the user can decide to change the action anyway
        this.feedbackService.customizationActionError(error, 5000).then(isConfirmed => {
          if (isConfirmed) {
            const args_err = new PostRequestArguments({
              url: [this.url, 'overwriteItemStateTransitions', itemTypeID],
              object: itemStateTransition,
            });
            const call_err = this.api.postRequest(args_err);
            call_err.subscribe(
              itemStateTransitions => {
                this.api.updateObservable(itemStateTransitions, this.$itemStateTransitions);
                this.api.getCacheUrl([this.url, 'itemStateTransitions']).then(
                  url => { this.cache.saveItem(url, itemStateTransitions, this.groupKey, 60*60 * 24); }
                ); // not sure about this part of the cache
              })
              this.navController.navigateBack(['customization', 'workflowActions']).then(() => {});
          }
          else {
            // Handle 'No' button action or dialog dismissal
          }
      });
    });
    return call;
  }

  public deleteItemStateTransition(ItemStateTransitionId) {
    const args = new PostRequestArguments({
      url: [this.url, 'itemStateTransitions'],
      object: ItemStateTransitionId,
    });
    const call = this.api.deleteRequest(args);
    call.subscribe(
      id => {
        if (typeof id === 'string') {
          const index = this.$itemStateTransitions.value.findIndex(c => c.id === parseInt(id, 10));
          this.$itemStateTransitions.value.splice(index, 1);
        }
        this.feedbackService.message('The action was successfully deleted', 'Success');
      },
      error => {
        this.feedbackService.standardError(error);
      });
    return call;
  }


  public loadItemStates(refresh: Refresh = 'expired', itemTypeID) {
    const args = new GetRequestArguments({
      url: [this.url, 'itemStates', itemTypeID],
      cacheGroup: this.groupKey,
      ttl: this.hour * 24,
      refresh
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(itemStates => {
      this.api.updateObservable(itemStates, this.$itemStates);
    });
    return call;
  }

  /*
  * -----------------------------------------------------
  * -----------------      VAT Rates     ----------------
  * -----------------------------------------------------
  */

  public loadVatRates(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: [this.url, 'vatRates'],
      cacheGroup: this.groupKey,
      ttl: this.hour * 24,
      refresh
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(VatRates => {
      this.api.updateObservable(VatRates, this.$vatRates);
    }); //automatically reload when new data arrives from API
    return call;
  }

  public loadVatRate(vatRateId) {
    const args = new GetRequestArguments({
      url: [this.url, 'vatRate', vatRateId], // back end
      cacheGroup: undefined,
    });
    this.api.cachedGetRequest(args).subscribe(
      vatRate => {
        const form = this.createVatRateForm();
        this.fillVatRateForm(form, vatRate);
        this.api.updateObservable(form, this.$editingVatRate);
      },
      error => {
        this.feedbackService.standardError(error, 4000, 'top').then( () => {
          this.navController.navigateBack(['/', 'customization', 'VATRates']);
        });
      });
  }

  public createVatRateForm() {
    return this.formBuilder.group({
      default: [undefined, []],
      id: [undefined, []],
      name: [undefined, [Validators.required, Validators.minLength(2)]],
      display_name: [undefined, []],
      rate: [undefined, [this.validateRate]],
      active: [undefined, ],
      country_id: [undefined, []],
      created_at: [undefined, []],
      updated_at: [undefined, []],
    });
  }

  // This is used to validate the rate field
  private validateRate(control: UntypedFormControl) {
    const rateValue = control.value;
    if (rateValue && !/^(\d+(\.\d+)?)?$/.test(rateValue)) {
      return { invalidRate: true };
    }
    return null;
  }

  public fillVatRateForm(form, vatRate) {
    if (vatRate !== undefined && vatRate !== null) {
      form.controls.default.patchValue(vatRate.default);
      form.controls.id.patchValue(parseInt(vatRate.id, 10));
      form.controls.name.patchValue(vatRate.name);
      form.controls.display_name.patchValue(vatRate.display_name);
      form.controls.rate.patchValue(vatRate.rate);
      form.controls.active.patchValue(vatRate.active);
      form.controls.country_id.patchValue(vatRate.country_id);
      form.controls.created_at.patchValue(vatRate.created_at);
      form.controls.updated_at.patchValue(vatRate.updated_at);
    }
  }

  public createVatRateFormData(vatRate) {
    return this.api.createFormData(vatRate);
  }

  public postVatRate(vatRate) {
    const args = new PostRequestArguments({
      url: [this.url, 'vatRates'],
      object: vatRate,
    });
    const call = this.api.postRequest(args);
    call.subscribe(
      VatRates => {
        this.api.updateObservable(VatRates, this.$vatRates);
        this.api.getCacheUrl([this.url, 'vatRates']).then(
          url => { this.cache.saveItem(url, VatRates, this.groupKey, 60*60 * 24); }
        );
      },

      error => { this.feedbackService.customizationActionError(error, 5000);
    });
    return call;
  }

  public deleteVatRate(vatRateId) {
    const args = new PostRequestArguments({
      url: [this.url, 'vatRates', ],
      object: vatRateId,
    });
    const call = this.api.deleteRequest(args);
    call.subscribe(
      id => {
        if (typeof id === 'string') {
          const index = this.$vatRates.value.findIndex(c => c.id === parseInt(id, 10));
          this.$vatRates.value.splice(index, 1); // With splice remove the element from vatRates correspondng to the ID
        }
        this.feedbackService.message('The VAT rate was successfully deleted', 'Success');
      },
      error => {
        this.feedbackService.standardError(error);
      });
    return call;
  }

  public getVatRatesBexio() {
    const args = new GetRequestArguments({
      url: [this.url, 'vatRatesBexio'],
      cacheGroup: this.groupKey,
      ttl: this.hour * 24,
      refresh: 'all',
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(
      VatRates => {
        this.api.updateObservable(VatRates, this.$vatRates);
      },
      error => {
        this.feedbackService.standardError(error);
      }
    );
    return call;
  }

  /*
  * -----------------------------------------------------
  * -----------------      Access Rights     ------------
  * -----------------------------------------------------
  */

  public loadRoles(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: [this.url, 'roles'],
      cacheGroup: this.groupKey,
      ttl: this.hour * 24,
      refresh
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(Roles => {
      this.api.updateObservable(Roles, this.$roles);
    });
    return call;
  }


  public loadUsers(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: [this.url, 'users'],
      cacheGroup: this.groupKey,
      ttl: this.hour * 24,
      refresh
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(Users => {
      this.api.updateObservable(Users, this.$users);
    });
    return call;
  }



  // This method is called when the page is reloaded
  public loadUser(userId) {
    const args = new GetRequestArguments({
      url: [this.url, 'user', userId],
      cacheGroup: undefined,
    });
    this.api.cachedGetRequest(args).subscribe(
      user => {
        const form = this.createRoleUserForm();
        this.fillRoleUserForm(form, user);
        this.api.updateObservable(form, this.$editingRoleUser);
      },
      error => {
        this.feedbackService.standardError(error, 4000, 'top').then( () => {
          this.navController.navigateBack(['/', 'customization', 'roleUser']);
        });
      });
  }

  public createRoleUserForm() {
    return this.formBuilder.group({
      default: [undefined, []],
      id: [undefined, []],
      active: [undefined, []],
      email: [undefined, []],
      roleID: [undefined, [Validators.required]],
      created_at: [undefined, []],
      updated_at: [undefined, []],
    });
  }

  public fillRoleUserForm(form, user) {
    if (user !== undefined && user !== null) {
      form.controls.default.patchValue(user.default);
      form.controls.id.patchValue(parseInt(user.id, 10));
      form.controls.active.patchValue(parseInt(user.active, 10));
      form.controls.email.patchValue(user.email);
      form.controls.roleID.patchValue(user.role?.id || ''); // This command should be useless if to every user is associated a role, which is the case
      form.controls.created_at.patchValue(user.created_at);
      form.controls.updated_at.patchValue(user.updated_at);
    }
  }

  public createRoleUserFormData(roleUser) {
    return this.api.createFormData(roleUser);
  }

  public postRoleUser(roleUser) {
    const args = new PostRequestArguments({
      url: [this.url, 'roleUser'],
      object: roleUser,
    });
    const call = this.api.postRequest(args);
    call.subscribe(

      RoleUser => {
        this.api.updateObservable(RoleUser, this.$users);
        this.api.getCacheUrl([this.url, 'roleUser']).then(
          url => { this.cache.saveItem(url, RoleUser, this.groupKey, 60*60 * 24); }
        );
      },

      error => { this.feedbackService.customizationActionError(error, 5000);
    });
    return call;
  }

/*
  * -----------------------------------------------------
  * -----------------     API Tokens     ----------------
  * -----------------------------------------------------
  */

  public loadAPITokens(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: [this.url, 'APITokens'],
      cacheGroup: this.groupKey,
      ttl: this.hour * 24,
      delayed: false,
      refresh
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(APITokens => {
      this.api.updateObservable(APITokens, this.$apiTokens);
    });
    return call;
  }

  public loadAPIToken(APITokenID) {
    const args = new GetRequestArguments({
      url: [this.url, 'APIToken', APITokenID],
      cacheGroup: undefined,
    });
    this.api.cachedGetRequest(args).subscribe(
      APIToken => {
        const form = this.createAPITokenForm();
        this.fillAPITokenForm(form, APIToken);
        this.api.updateObservable(form, this.$editingAPIToken);
      },
      error => {
        this.feedbackService.standardError(error, 4000, 'top').then( () => {
          this.navController.navigateBack(['/', 'customization', 'APITokens']);
        });
      });
  }

  public createAPITokenForm() {
    return this.formBuilder.group({
      id: [undefined, []],
      key: [undefined, []],
      value: [undefined, []],
      active: [undefined, []],
    });
  }

  public fillAPITokenForm(form, APIToken) {
    if (APIToken !== undefined && APIToken !== null) {
      form.controls.id.patchValue(parseInt(APIToken.id, 10));
      form.controls.key.patchValue(APIToken.key);
      form.controls.value.patchValue(APIToken.value);
      form.controls.active.patchValue(APIToken.active);
    }
  }

  public createAPITokenFormData(APIToken) {
    return this.api.createFormData(APIToken);
  }

  public postAPIToken(APIToken) {
    const args = new PostRequestArguments({
      url: [this.url, 'APITokens'],
      object: APIToken,
    });
    const call = this.api.postRequest(args);
    call.subscribe(

      APITokens => {
        this.api.updateObservable(APITokens, this.$apiTokens);
        this.api.getCacheUrl([this.url, 'APITokens']).then(
          url => { this.cache.saveItem(url, APITokens, this.groupKey, 60*60 * 24); }
        );
      },

      error => { this.feedbackService.customizationActionError(error, 5000);
    });
    return call;
  }
}
